import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { getWorkCenters, getWorkers, getVacationRequests, getHolidays, getVacationTypes, createVacationRequest, updateVacationRequest, deleteVacationRequest, getWorkersVacationSummary, getWorkerAvailableHours, getWorkSchedules } from '../services/api';
import { WorkCenter, Worker, VacationType, VacationRequest, Holiday, VacationStatus, WorkerVacationSummary } from '../types';
import { workCenterAnnualBagsApi } from '../services/workCenterAnnualBagsApi';

type DayStatus = 'holiday' | 'vacation' | 'vacation-previous' | 'work' | 'weekend' | 'pending' | 'partial' | 'partial-previous' | 'flexible';

interface DayInfo {
  date: string;
  status: DayStatus;
  description?: string;
  partialHours?: number;
  workHours?: number;
  vacationBagYear?: number;
}

const VacationCalendar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
  const [vacationRequests, setVacationRequests] = useState<VacationRequest[]>([]);
  const [vacationTypes, setVacationTypes] = useState<VacationType[]>([]);
  const [vacationSummaries, setVacationSummaries] = useState<WorkerVacationSummary[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedBagYear, setSelectedBagYear] = useState<number>(new Date().getFullYear());
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedVacationType, setSelectedVacationType] = useState<number | null>(null);
  const [hours, setHours] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [createdRequest, setCreatedRequest] = useState<any>(null);
  const [availableHours, setAvailableHours] = useState<{work_hours: number, available_hours: number, used_hours: number} | null>(null);
  const [dialogBagYear, setDialogBagYear] = useState<number>(new Date().getFullYear());

  // States for vacation deletion
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vacationToDelete, setVacationToDelete] = useState<VacationRequest | null>(null);

  // Check if selected month allows selecting previous year bag (Jan-Mar)
  const shouldShowBagSelector = useCallback(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    
    // If viewing current year and we're in Jan-Mar, show selector
    if (selectedYear === currentYear && selectedMonth + 1 >= 1 && selectedMonth + 1 <= 3) {
      return true;
    }
    
    // If viewing a future year, always show selector for Jan-Mar
    if (selectedYear > currentYear && selectedMonth + 1 >= 1 && selectedMonth + 1 <= 3) {
      return true;
    }
    
    return false;
  }, [selectedYear, selectedMonth]);

  // Check if dialog should show bag selector for the selected date
  const shouldShowDialogBagSelector = () => {
    if (!selectedDate) return false;
    const date = new Date(selectedDate);
    const month = date.getMonth() + 1;
    return month >= 1 && month <= 3;
  };

  // Get available days for a specific worker and bag year
  const getAvailableDays = (worker: Worker | null, bagYear: number) => {
    if (!worker) return null;
    
    const summary = vacationSummaries.find(s => s.worker_id === worker.id);
    if (!summary) return null;
    
    return {
      available_days: summary.remaining_vacation_days,
      used_days: summary.used_vacation_days,
      total_days: worker.annual_vacation_days
    };
  };

  // Generate date range for the selected month
  const generateDateRange = (year: number, month: number) => {
    const dates: string[] = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  // Get day status for a worker on a specific date
  const getDayStatus = (worker: Worker, date: string): DayInfo => {
    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay();
    
    // Check if it's weekend
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return { date, status: 'weekend', description: 'Fin de semana' };
    }
    
    // Check if it's a holiday for this work center (only from database)
    const holiday = holidays.find(h => 
      h.date === date && 
      (h.work_center_id === worker.work_center_id || h.is_national)
    );
    if (holiday) {
      return { date, status: 'holiday', description: holiday.name };
    }
    
    // Check if worker has vacation requests for this day
    // First check for pending vacations
    const pendingVacations = vacationRequests.filter(v => 
      v.worker_id === worker.id && 
      v.status === VacationStatus.PENDING &&
      new Date(v.start_date) <= dateObj && 
      new Date(v.end_date) >= dateObj
    );
    
    if (pendingVacations.length > 0) {
      return { date, status: 'pending', description: 'Pendiente' };
    }
    
    // Then check for approved vacations
    const approvedVacations = vacationRequests.filter(v => 
      v.worker_id === worker.id && 
      v.status === VacationStatus.APPROVED &&
      new Date(v.start_date) <= dateObj && 
      new Date(v.end_date) >= dateObj
    );
    
    if (approvedVacations.length > 0) {
      // Check for vacation type 2 (Horas Flexibles) first - always show as partial/green
      const hourlyVacations = approvedVacations.filter(v => v.vacation_type_id === 2);
      
      if (hourlyVacations.length > 0) {
        const totalHours = hourlyVacations.reduce((sum, v) => sum + (v.hours_requested || 0), 0);
        const firstVacation = hourlyVacations[0];
        const isPreviousYearBag = firstVacation.vacation_bag_year && firstVacation.vacation_bag_year < dateObj.getFullYear();
        
        return { 
          date, 
          status: isPreviousYearBag ? 'partial-previous' : 'partial', 
          description: `${totalHours}h`,
          partialHours: totalHours,
          workHours: 8,
          vacationBagYear: firstVacation.vacation_bag_year
        };
      }
      
      // Check for vacation type 1 (Días Completos) - show as blue or purple depending on bag year
      const fullDayVacations = approvedVacations.filter(v => v.vacation_type_id === 1);
      
      if (fullDayVacations.length > 0) {
        const firstVacation = fullDayVacations[0];
        const isPreviousYearBag = firstVacation.vacation_bag_year && firstVacation.vacation_bag_year < dateObj.getFullYear();
        
        return { 
          date, 
          status: isPreviousYearBag ? 'vacation-previous' : 'vacation', 
          description: 'Vacaciones',
          vacationBagYear: firstVacation.vacation_bag_year
        };
      }
    }
    
    return { date, status: 'work', description: 'Trabaja' };
  };

  const getStatusLabel = (dayInfo: DayInfo) => {
    switch (dayInfo.status) {
      case 'holiday': return 'F';
      case 'vacation': return 'V';
      case 'vacation-previous': return 'V';
      case 'flexible': return `${dayInfo.partialHours}h`;
      case 'partial': return `${dayInfo.partialHours}h`;
      case 'partial-previous': return `${dayInfo.partialHours}h`;
      case 'pending': return '?';
      case 'work': return '';  // Empty for work days
      case 'weekend': return '';
      default: return '';
    }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [workCentersData, workersData, requestsData, holidaysData, vacationTypesData, vacationSummariesData, annualBagsData] = await Promise.all([
          getWorkCenters(),
          getWorkers(),
          getVacationRequests(),
          getHolidays(undefined, selectedYear),
          getVacationTypes(),
          getWorkersVacationSummary(selectedYear, shouldShowBagSelector() ? selectedBagYear : undefined),
          workCenterAnnualBagsApi.getAll()
        ]);
        
        // Fetch work schedules separately (not needed for years calculation)
        await getWorkSchedules();
        
        setWorkCenters(workCentersData);
        setWorkers(workersData);
        setVacationRequests(requestsData);
        setHolidays(holidaysData);
        setVacationTypes(vacationTypesData);
        setVacationSummaries(vacationSummariesData);
        
        // Extraer años únicos de las bolsas anuales y ordenarlos
        const years = Array.from(new Set(annualBagsData.map((bag: any) => bag.year))).sort((a: number, b: number) => a - b);
        setAvailableYears(years);
        
        setDateRange(generateDateRange(selectedYear, selectedMonth));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, selectedMonth, selectedBagYear, shouldShowBagSelector]);

  // Handle month/year changes
  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(event.target.value as number);
  };

  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setSelectedMonth(event.target.value as number);
  };

  const getMonthName = (month: number) => {
    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return monthNames[month];
  };

  // Navigation functions
  const navigateMonth = (direction: 'prev' | 'next') => {
    let newMonth = selectedMonth;
    let newYear = selectedYear;
    
    if (direction === 'prev') {
      newMonth--;
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      }
    } else {
      newMonth++;
      if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const getDayOfWeek = (dateStr: string) => {
    const date = new Date(dateStr);
    const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    return days[date.getDay()];
  };

  const isStartOfWeek = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.getDay() === 1; // Monday
  };

  const isWeekend = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  const getWorkCenterName = (workCenterId: number) => {
    const center = workCenters.find(wc => wc.id === workCenterId);
    return center ? center.name : '';
  };

  // Handle cell click to create vacation
  const handleCellClick = async (worker: Worker, date: string) => {
    const dayInfo = getDayStatus(worker, date);
    
    // If it's a pending vacation, handle approval
    if (dayInfo.status === 'pending') {
      const pendingVacation = vacationRequests.find(v => 
        v.worker_id === worker.id && 
        v.status === VacationStatus.PENDING &&
        new Date(v.start_date) <= new Date(date) && 
        new Date(v.end_date) >= new Date(date)
      );
      
      if (pendingVacation) {
        setCreatedRequest(pendingVacation);
        setShowConfirmation(true);
        setDialogOpen(true);
        return;
      }
    }

    // If clicking on a vacation day (approved or flexible), offer to delete it
    if (dayInfo.status === 'vacation' || dayInfo.status === 'vacation-previous' || dayInfo.status === 'flexible' || dayInfo.status === 'partial' || dayInfo.status === 'partial-previous') {
      const vacation = vacationRequests.find(req => 
        req.worker_id === worker.id &&
        (req.status === VacationStatus.APPROVED) &&
        new Date(req.start_date) <= new Date(date) &&
        new Date(req.end_date) >= new Date(date)
      );
      
      if (vacation) {
        setVacationToDelete(vacation);
        setDeleteDialogOpen(true);
        return;
      }
    }
    
    // Don't allow vacation creation on weekends or holidays
    if (dayInfo.status === 'weekend' || dayInfo.status === 'holiday') {
      return;
    }
    
    setSelectedWorker(worker);
    setSelectedDate(date);
    setSelectedVacationType(null);
    setHours(null);
    
    // Set dialog bag year based on date
    const requestDate = new Date(date);
    const requestMonth = requestDate.getMonth() + 1;
    const requestYear = requestDate.getFullYear();
    
    if (requestMonth >= 1 && requestMonth <= 3) {
      // Default to previous year for Jan-Mar
      setDialogBagYear(requestYear - 1);
    } else {
      setDialogBagYear(requestYear);
    }
    
    // Get available hours for this worker and date
    try {
      const hoursData = await getWorkerAvailableHours(worker.id, date);
      setAvailableHours(hoursData);
    } catch (error) {
      console.error('Error getting available hours:', error);
      setAvailableHours(null);
    }
    
    setDialogOpen(true);
  };

  // Handle vacation creation
  const handleCreateVacation = async () => {
    if (!selectedWorker || !selectedDate || !selectedVacationType) return;
    
    setSubmitting(true);
    try {
      const vacationType = vacationTypes.find(vt => vt.id === selectedVacationType);
      
      // Determine vacation bag year (same logic as in VacationRequests.tsx)
      const requestDate = new Date(selectedDate);
      const requestYear = requestDate.getFullYear();
      const requestMonth = requestDate.getMonth() + 1;
      
      // Check if we're in carryover period (Jan 1 - Mar 31) and should use selected bag
      const isCarryoverPeriod = () => {
        return requestMonth >= 1 && requestMonth <= 3;
      };
      
      let bagYear = requestYear;
      if (!vacationType?.is_hourly && isCarryoverPeriod() && shouldShowDialogBagSelector()) {
        // Use the selected bag year from dialog when in carryover period
        bagYear = dialogBagYear;
      }
      
      const newRequest = {
        worker_id: selectedWorker.id,
        vacation_type_id: selectedVacationType,
        start_date: selectedDate,
        end_date: selectedDate,
        hours_requested: vacationType?.is_hourly && hours !== null ? hours : undefined,
        notes: '',
        vacation_bag_year: bagYear
      };
      
      const created = await createVacationRequest(newRequest);
      console.log('Created request:', created);
      setCreatedRequest(created);
      setShowConfirmation(true);
      setSubmitting(false); // Reset submitting state
      console.log('showConfirmation set to true');
      
    } catch (error: any) {
      console.error('Error creating vacation:', error);
      
      // Extract error message from the response
      let errorMessage = 'Error al crear la vacación';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
      setSubmitting(false);
    }
  };

  // Handle vacation approval
  const handleApproveVacation = async () => {
    if (!createdRequest) return;
    
    setSubmitting(true);
    try {
      // Update status to approved
      await updateVacationRequest(createdRequest.id, { status: VacationStatus.APPROVED });
      
      // Refresh vacation requests to show the updated one
      const [updatedRequests, updatedSummaries] = await Promise.all([
        getVacationRequests(),
        getWorkersVacationSummary(selectedYear)
      ]);
      setVacationRequests(updatedRequests);
      setVacationSummaries(updatedSummaries);
      
      handleDialogClose();
    } catch (error: any) {
      console.error('Error approving vacation:', error);
      
      // Extract error message from the response
      let errorMessage = 'Error al aprobar la vacación';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectVacation = async () => {
    if (!createdRequest) {
      handleDialogClose();
      return;
    }
    
    setSubmitting(true);
    try {
      // Update status to rejected
      await updateVacationRequest(createdRequest.id, { status: VacationStatus.REJECTED });
      
      // Refresh vacation summaries
      const updatedSummaries = await getWorkersVacationSummary(selectedYear);
      setVacationSummaries(updatedSummaries);
      
      handleDialogClose();
    } catch (error: any) {
      console.error('Error rejecting vacation:', error);
      
      // Extract error message from the response
      let errorMessage = 'Error al rechazar la vacación';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVacation = async () => {
    if (!vacationToDelete) return;
    
    setSubmitting(true);
    try {
      await deleteVacationRequest(vacationToDelete.id);
      
      // Refresh vacation requests and summaries
      const [updatedRequests, updatedSummaries] = await Promise.all([
        getVacationRequests(),
        getWorkersVacationSummary(selectedYear)
      ]);
      setVacationRequests(updatedRequests);
      setVacationSummaries(updatedSummaries);
      
      setDeleteDialogOpen(false);
      setVacationToDelete(null);
    } catch (error: any) {
      console.error('Error deleting vacation:', error);
      
      // Extract error message from the response
      let errorMessage = 'Error al borrar la vacación';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setVacationToDelete(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedWorker(null);
    setSelectedDate('');
    setSelectedVacationType(null);
    setHours(null);
    setShowConfirmation(false);
    setCreatedRequest(null);
    setSubmitting(false);
    setAvailableHours(null);
    setDialogBagYear(new Date().getFullYear());
  };

  // Calculate working people per day
  const getWorkingPeoplePerDay = (date: string) => {
    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay();
    
    // Si es fin de semana, no calculamos cobertura laboral
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return -1; // Valor especial para indicar que es fin de semana
    }
    
    // Check if it's holiday for all workers (only from database)
    const allWorkersHaveHoliday = workers.every(worker => {
      const dayInfo = getDayStatus(worker, date);
      return dayInfo.status === 'holiday';
    });
    
    // Si es festivo para todos los trabajadores, no calculamos cobertura
    if (workers.length > 0 && allWorkersHaveHoliday) {
      return -2; // Valor especial para indicar que es festivo para todos
    }
    
    let workingCount = 0;
    workers.forEach(worker => {
      const dayInfo = getDayStatus(worker, date);
      if (dayInfo.status === 'work') {
        workingCount++;
      }
    });
    return workingCount;
  };

  const getWorkingSummaryColor = (workingCount: number, totalWorkers: number) => {
    if (workingCount === -1) return 'default'; // Fin de semana
    if (workingCount === -2) return 'default'; // Festivo para todos
    if (workingCount === 0) return 'error'; // Rojo si nadie trabaja en día laborable
    if (workingCount === 1) return 'warning'; // Amarillo si solo 1 persona
    if (workingCount < totalWorkers * 0.5) return 'info'; // Azul si menos del 50%
    return 'success'; // Verde si buena cobertura
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <Typography 
          sx={{ 
            color: '#666666',
            fontWeight: 300,
            fontSize: '0.9rem'
          }}
        >
          Cargando análisis...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'start' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 }, mb: { xs: 2, sm: 6 } }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1,
              fontWeight: 700,
              color: '#001923',
              letterSpacing: '-0.01em',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Calendario Mensual
          </Typography>
          
          <Typography 
            variant="body1" 
            className="descripcion-cyan"
            sx={{ 
              fontWeight: 300,
              lineHeight: 1.6
            }}
          >
            Análisis visual de cobertura del equipo
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={() => navigateMonth('prev')} 
              sx={{
                border: '1px solid #f0f0f0',
                borderRadius: 0,
                color: '#666666',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#fafafa',
                  borderColor: '#e0e0e0'
                }
              }}
            >
              <ArrowBackIos sx={{ fontSize: 14 }} />
            </IconButton>
            
            <IconButton 
              onClick={() => navigateMonth('next')}
              sx={{
                border: '1px solid #f0f0f0',
                borderRadius: 0,
                color: '#666666',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#fafafa',
                  borderColor: '#e0e0e0'
                }
              }}
            >
              <ArrowForwardIos sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          
          <FormControl 
            sx={{ 
              minWidth: { xs: 'calc(50% - 8px)', sm: 120 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                fontWeight: 300
              },
              '& .MuiInputLabel-root': {
                fontWeight: 300,
                color: '#666666'
              }
            }}
          >
          <InputLabel>Año</InputLabel>
          <Select
            value={selectedYear}
            label="Año"
            onChange={handleYearChange}
          >
            {availableYears.map((year: number) => (
              <MenuItem key={year} value={year} sx={{ fontWeight: 300 }}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl 
          sx={{ 
            minWidth: { xs: 'calc(50% - 8px)', sm: 140 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              fontWeight: 300
            },
            '& .MuiInputLabel-root': {
              fontWeight: 300,
              color: '#666666'
            }
          }}
        >
          <InputLabel>Mes</InputLabel>
          <Select
            value={selectedMonth}
            label="Mes"
            onChange={handleMonthChange}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i} value={i} sx={{ fontWeight: 300 }}>
                {getMonthName(i)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Bag Year Selector for Jan-Mar */}
        {shouldShowBagSelector() && (
          <FormControl 
            sx={{ 
              minWidth: { xs: '100%', sm: 180 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                fontWeight: 300
              },
              '& .MuiInputLabel-root': {
                fontWeight: 300,
                color: '#666666'
              }
            }}
          >
            <InputLabel>Bolsa de días</InputLabel>
            <Select
              value={selectedBagYear}
              label="Bolsa de días"
              onChange={(e) => setSelectedBagYear(e.target.value as number)}
            >
              <MenuItem value={selectedYear - 1} sx={{ fontWeight: 300 }}>
                Año {selectedYear - 1} (anterior)
              </MenuItem>
              <MenuItem value={selectedYear} sx={{ fontWeight: 300 }}>
                Año {selectedYear} (actual)
              </MenuItem>
            </Select>
          </FormControl>
          )}
        </Box>
      </Box>
      
      <Typography 
        variant="body1" 
        sx={{ 
          mb: 2,
          color: '#1a1a1a',
          fontWeight: 300,
          fontSize: '1rem'
        }}
      >
        {getMonthName(selectedMonth)} {selectedYear}
      </Typography>

      {/* Bag Information */}
      {shouldShowBagSelector() && (
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 3,
            color: '#666666',
            fontWeight: 300,
            fontSize: '0.9rem'
          }}
        >
          Usando bolsa de días del año {selectedBagYear}
        </Typography>
      )}

      <TableContainer 
        component={Paper} 
        elevation={0}
        sx={{ 
          border: '1px solid #f0f0f0',
          borderRadius: 0,
          flex: 1,
          height: { xs: 'calc(100vh - 320px)', sm: 'calc(100vh - 200px)' },
          minHeight: 300,
          overflowX: 'auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          '& .MuiTable-root': {
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            minWidth: 'max-content'
          },
          '& .MuiTableCell-head': {
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #f0f0f0',
            fontSize: { xs: '9px', sm: '11px' },
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#666666',
            padding: { xs: '8px 4px', sm: '12px 8px' },
            position: 'sticky',
            top: 0,
            zIndex: 10
          },
          '& .MuiTableCell-body': {
            borderBottom: '1px solid #f8f8f8',
            color: '#1a1a1a',
            fontWeight: 300,
            fontSize: { xs: '11px', sm: '14px' },
            padding: { xs: '4px', sm: '8px' }
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: '#fafafa'
          },
          // Sticky first three columns - en móvil solo la primera es sticky
          '& .sticky-column-1': {
            position: 'sticky',
            left: 0,
            backgroundColor: '#ffffff',
            zIndex: 5,
            borderRight: '2px solid #e0e0e0',
            minWidth: { xs: '100px', sm: '150px' },
            maxWidth: { xs: '100px', sm: '150px' }
          },
          '& .sticky-column-2': {
            position: { xs: 'static', sm: 'sticky' },
            left: { xs: 'auto', sm: '150px' },
            backgroundColor: '#ffffff',
            zIndex: { xs: 0, sm: 5 },
            borderRight: { xs: '1px solid #f0f0f0', sm: '2px solid #e0e0e0' },
            minWidth: { xs: '80px', sm: '100px' },
            maxWidth: { xs: '80px', sm: '100px' }
          },
          '& .sticky-column-3': {
            position: { xs: 'static', sm: 'sticky' },
            left: { xs: 'auto', sm: '250px' },
            backgroundColor: '#ffffff',
            zIndex: { xs: 0, sm: 5 },
            borderRight: { xs: '1px solid #f0f0f0', sm: '2px solid #e0e0e0' },
            minWidth: { xs: '90px', sm: '130px' },
            maxWidth: { xs: '90px', sm: '130px' }
          },
          // Header sticky columns - en móvil solo la primera es sticky
          '& .sticky-header-1': {
            position: 'sticky',
            left: 0,
            backgroundColor: '#fafafa',
            zIndex: 15,
            borderRight: '2px solid #e0e0e0'
          },
          '& .sticky-header-2': {
            position: { xs: 'static', sm: 'sticky' },
            left: { xs: 'auto', sm: '150px' },
            backgroundColor: '#fafafa',
            zIndex: { xs: 10, sm: 15 },
            borderRight: { xs: '1px solid #f0f0f0', sm: '2px solid #e0e0e0' }
          },
          '& .sticky-header-3': {
            position: { xs: 'static', sm: 'sticky' },
            left: { xs: 'auto', sm: '250px' },
            backgroundColor: '#fafafa',
            zIndex: { xs: 10, sm: 15 },
            borderRight: { xs: '1px solid #f0f0f0', sm: '2px solid #e0e0e0' }
          }
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className="sticky-header-1" sx={{ minWidth: { xs: 100, sm: 120 } }} rowSpan={2}>
                {isMobile ? 'Trab.' : 'Trabajador'}
              </TableCell>
              <TableCell className="sticky-header-2" sx={{ minWidth: { xs: 80, sm: 80 } }} rowSpan={2}>Centro</TableCell>
              <TableCell className="sticky-header-3" sx={{ minWidth: { xs: 90, sm: 130 } }} rowSpan={2}>
                {isMobile ? 'Bolsa' : 'Bolsa'}
              </TableCell>
              {dateRange.map((date) => (
                <TableCell 
                  key={date} 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    minWidth: 70,
                    fontSize: '0.75rem',
                    padding: '4px',
                    borderLeft: isStartOfWeek(date) ? '2px solid #1976d2' : 'none',
                    backgroundColor: isWeekend(date) ? '#f5f5f5' : 'inherit'
                  }}
                >
                  {formatDate(date)}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {dateRange.map((date) => (
                <TableCell 
                  key={`day-${date}`} 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    fontSize: '0.7rem',
                    padding: '2px',
                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                    borderLeft: isStartOfWeek(date) ? '2px solid #1976d2' : 'none',
                    backgroundColor: isWeekend(date) ? '#f5f5f5' : 'inherit',
                    color: isWeekend(date) ? '#666' : 'inherit'
                  }}
                >
                  {getDayOfWeek(date)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {workers.map((worker) => (
              <TableRow key={worker.id}>
                <TableCell className="sticky-column-1" sx={{ fontWeight: 'medium', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  {isMobile 
                    ? `${worker.name} ${worker.surname ? worker.surname.charAt(0) + '.' : ''}` 
                    : `${worker.name} ${worker.surname || ''}`
                  }
                </TableCell>
                <TableCell className="sticky-column-2" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  {getWorkCenterName(worker.work_center_id)}
                </TableCell>
                <TableCell className="sticky-column-3" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  {(() => {
                    const summary = vacationSummaries.find(vs => vs.worker_id === worker.id);
                    if (!summary) return isMobile ? 'N/D' : 'No disponible';
                    
                    const remainingDays = summary.annual_vacation_days - summary.used_vacation_days;
                    const remainingHours = summary.annual_flexible_hours - summary.used_flexible_hours;
                    
                    return (
                      <Box>
                        <div>{isMobile ? 'D:' : 'Días:'} {remainingDays}/22</div>
                        <div>{isMobile ? 'H:' : 'Horas:'} {remainingHours}/58</div>
                      </Box>
                    );
                  })()}
                </TableCell>
                {dateRange.map((date) => {
                  const dayInfo = getDayStatus(worker, date);
                  const isClickable = dayInfo.status === 'work' || dayInfo.status === 'partial' || dayInfo.status === 'partial-previous' || dayInfo.status === 'pending';
                  
                  return (
                    <TableCell 
                      key={date} 
                      align="center" 
                      sx={{ 
                        padding: '2px',
                        borderLeft: isStartOfWeek(date) ? '2px solid #1976d2' : 'none',
                        backgroundColor: isWeekend(date) ? '#f5f5f5' : 'inherit',
                        cursor: isClickable ? 'pointer' : 'default',
                        '&:hover': isClickable ? { 
                          backgroundColor: '#f5f5f5',
                          transform: 'scale(1.05)'
                        } : {}
                      }}
                      onClick={() => handleCellClick(worker, date)}
                    >
                      <Chip
                        label={getStatusLabel(dayInfo)}
                        size="small"
                        title={dayInfo.description}
                        sx={{ 
                          minWidth: '40px', 
                          height: '24px',
                          fontSize: '0.7rem',
                          // Apply direct colors based on status
                          ...(dayInfo.status === 'vacation' && {
                            backgroundColor: '#2196F3', // Blue for full vacation days (current year)
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#1976D2'
                            }
                          }),
                          ...(dayInfo.status === 'vacation-previous' && {
                            backgroundColor: '#9C27B0', // Purple for full vacation days (previous year)
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#7B1FA2'
                            }
                          }),
                          ...(dayInfo.status === 'partial' && {
                            backgroundColor: '#4CAF50', // Green for partial hours (current year)
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#45a049'
                            }
                          }),
                          ...(dayInfo.status === 'partial-previous' && {
                            backgroundColor: '#FF9800', // Orange for partial hours (previous year)
                            color: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#F57C00'
                            }
                          }),
                          ...(dayInfo.status === 'pending' && {
                            backgroundColor: '#fff3cd', // Yellow for pending vacations
                            color: '#856404',
                            '&:hover': {
                              backgroundColor: '#ffeaa7'
                            }
                          }),
                          ...(dayInfo.status === 'work' && {
                            backgroundColor: 'transparent', // Empty for work days
                            color: 'transparent',
                            border: 'none',
                            '&:hover': {
                              backgroundColor: '#f0f0f0'
                            }
                          }),
                          ...(dayInfo.status === 'holiday' && {
                            backgroundColor: '#f8d7da',
                            color: '#721c24'
                          }),
                          ...(dayInfo.status === 'weekend' && {
                            backgroundColor: 'transparent',
                            color: 'transparent',
                            border: 'none'
                          })
                        }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            
            {/* Fila de resumen */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', borderTop: 2 }}>
              <TableCell className="sticky-column-1" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                RESUMEN
              </TableCell>
              <TableCell className="sticky-column-2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                Personas trabajando
              </TableCell>
              <TableCell className="sticky-column-3" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                {/* Columna vacía para alinear con bolsa de vacaciones */}
              </TableCell>
              {dateRange.map((date) => {
                const workingCount = getWorkingPeoplePerDay(date);
                const summaryColor = getWorkingSummaryColor(workingCount, workers.length);
                const isWeekendDay = workingCount === -1;
                const isUniversalHoliday = workingCount === -2;
                
                return (
                  <TableCell 
                    key={date} 
                    align="center" 
                    sx={{ 
                      padding: '2px',
                      borderLeft: isStartOfWeek(date) ? '2px solid #1976d2' : 'none',
                      backgroundColor: isWeekend(date) ? '#f5f5f5' : 'inherit'
                    }}
                  >
                    <Chip
                      label={isWeekendDay ? '' : isUniversalHoliday ? 'F' : workingCount}
                      color={summaryColor}
                      variant={workingCount === 0 ? 'filled' : 'outlined'}
                      size="small"
                      title={
                        isWeekendDay
                          ? 'Fin de semana - No hay actividad laboral'
                          : isUniversalHoliday
                            ? 'Festivo en todos los centros'
                            : workingCount === 0 
                              ? '¡ALERTA! Nadie trabajando este día'
                              : `${workingCount} de ${workers.length} personas trabajando`
                      }
                      sx={{ 
                        minWidth: '40px', 
                        height: '24px',
                        fontSize: '0.7rem',
                        fontWeight: (isWeekendDay || isUniversalHoliday) ? 'normal' : 'bold'
                      }}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ mr: { xs: 0, sm: 2 }, width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Leyenda:</Typography>
        <Chip label="✓ Trabaja" color="success" size="small" />
        <Chip 
          label="D Días completos (año actual)" 
          size="small" 
          sx={{
            backgroundColor: '#2196F3',
            color: '#ffffff',
            fontWeight: 600
          }}
        />
        <Chip 
          label="D Días completos (año anterior)" 
          size="small" 
          sx={{
            backgroundColor: '#9C27B0',
            color: '#ffffff',
            fontWeight: 600
          }}
        />
        <Chip 
          label="H Horas flexibles (año actual)" 
          size="small" 
          sx={{
            backgroundColor: '#4CAF50',
            color: '#ffffff',
            fontWeight: 600
          }}
        />
        <Chip 
          label="H Horas flexibles (año anterior)" 
          size="small" 
          sx={{
            backgroundColor: '#FF9800',
            color: '#ffffff',
            fontWeight: 600
          }}
        />
        <Chip 
          label="? Pendiente (click para aprobar)" 
          size="small" 
          sx={{
            backgroundColor: '#fff3cd',
            color: '#856404',
            fontWeight: 600
          }}
        />
        <Chip label="? Pendiente" color="info" size="small" />
        <Chip 
          label="F Festivo" 
          size="small" 
          sx={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            fontWeight: 600
          }}
        />
      </Box>
      
      <Box sx={{ mt: 1, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ mr: { xs: 0, sm: 2 }, width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Resumen cobertura:</Typography>
        <Chip label="0 = ¡ALERTA!" color="error" size="small" />
        <Chip label="1 = Mínimo" color="warning" size="small" />
        <Chip label="< 50% = Bajo" color="info" size="small" />
        <Chip label="≥ 50% = Bueno" color="success" size="small" />
      </Box>

      {/* Dialog for creating vacation */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {showConfirmation ? 'Confirmar Vacación' : 'Crear Vacación'}
        </DialogTitle>
        <DialogContent>
          {!showConfirmation ? (
            // Creation form
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Trabajador:</strong> {selectedWorker?.name} {selectedWorker?.surname}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Fecha:</strong> {selectedDate ? new Date(selectedDate).toLocaleDateString('es-ES', { 
                  weekday: 'long',
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                }) : ''}
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tipo de Vacación</InputLabel>
                <Select
                  value={selectedVacationType || ''}
                  label="Tipo de Vacación"
                  onChange={(e) => setSelectedVacationType(e.target.value as number)}
                >
                  {vacationTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name} - {type.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bag Year Selector for Jan-Mar dates */}
              {shouldShowDialogBagSelector() && selectedVacationType && !vacationTypes.find(vt => vt.id === selectedVacationType)?.is_hourly && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Bolsa de días a usar</InputLabel>
                  <Select
                    value={dialogBagYear}
                    label="Bolsa de días a usar"
                    onChange={(e) => setDialogBagYear(e.target.value as number)}
                  >
                    <MenuItem value={new Date(selectedDate).getFullYear() - 1}>
                      Año {new Date(selectedDate).getFullYear() - 1} (anterior)
                    </MenuItem>
                    <MenuItem value={new Date(selectedDate).getFullYear()}>
                      Año {new Date(selectedDate).getFullYear()} (actual)
                    </MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Available Days Information */}
              {selectedVacationType && !vacationTypes.find(vt => vt.id === selectedVacationType)?.is_hourly && (() => {
                const daysInfo = getAvailableDays(selectedWorker, dialogBagYear);
                return daysInfo && (
                  <Typography variant="body2" sx={{ mb: 2, color: '#666666', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <strong>Bolsa año {dialogBagYear}:</strong> {daysInfo.available_days} días disponibles 
                    ({daysInfo.used_days}/{daysInfo.total_days} usados)
                  </Typography>
                );
              })()}

              {selectedVacationType && vacationTypes.find(vt => vt.id === selectedVacationType)?.is_hourly && (
                <Box>
                  {availableHours && (
                    <Typography variant="body2" sx={{ mb: 1, color: '#666666' }}>
                      Jornada: {availableHours.work_hours}h | 
                      Usadas: {availableHours.used_hours}h | 
                      Disponibles: {availableHours.available_hours}h
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    label="Horas solicitadas"
                    type="number"
                    value={hours || ''}
                    onChange={(e) => setHours(Number(e.target.value))}
                    inputProps={{ 
                      min: 1, 
                      max: availableHours?.available_hours || 8 
                    }}
                    helperText={`Horas disponibles: ${availableHours?.available_hours || 'Calculando...'}`}
                    error={hours !== null && availableHours !== null && hours > availableHours.available_hours}
                  />
                </Box>
              )}
            </Box>
          ) : (
            // Confirmation view
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Resumen de la Solicitud
              </Typography>
              
              <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
                <Typography><strong>Trabajador:</strong> {selectedWorker?.name} {selectedWorker?.surname}</Typography>
                <Typography><strong>Fecha:</strong> {selectedDate ? new Date(selectedDate).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                }) : ''}</Typography>
                <Typography><strong>Tipo:</strong> {vacationTypes.find(vt => vt.id === selectedVacationType)?.name}</Typography>
                {createdRequest?.hours_requested && (
                  <Typography><strong>Horas:</strong> {createdRequest.hours_requested}h</Typography>
                )}
                <Box sx={{ mt: 1 }}>
                  <Typography component="span"><strong>Estado:</strong></Typography>
                  <Chip 
                    label="Pendiente de Aprobación" 
                    color="warning" 
                    size="small" 
                    sx={{ ml: 1 }}
                  />
                </Box>
              </Paper>
              
              <Typography variant="body2" color="text.secondary">
                ¿Desea aprobar esta solicitud de vacaciones?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!showConfirmation ? (
            <>
              <Button onClick={handleDialogClose}>Cancelar</Button>
              <Button 
                onClick={handleCreateVacation} 
                variant="contained" 
                disabled={!selectedVacationType || submitting}
              >
                {submitting ? 'Creando...' : 'Crear Vacación'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleRejectVacation} color="error">
                Rechazar
              </Button>
              <Button 
                onClick={handleApproveVacation} 
                variant="contained" 
                color="success"
                disabled={submitting}
              >
                {submitting ? 'Aprobando...' : 'Aprobar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog for deleting vacation */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Borrar Vacación
        </DialogTitle>
        <DialogContent>
          {vacationToDelete && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Trabajador:</strong> {workers.find(w => w.id === vacationToDelete.worker_id)?.name} {workers.find(w => w.id === vacationToDelete.worker_id)?.surname}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Fecha:</strong> {new Date(vacationToDelete.start_date).toLocaleDateString('es-ES', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Tipo:</strong> {vacationTypes.find(t => t.id === vacationToDelete.vacation_type_id)?.name}
              </Typography>
              {vacationToDelete.hours_requested && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Horas:</strong> {vacationToDelete.hours_requested}h
                </Typography>
              )}
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Estado:</strong> {vacationToDelete.status === 'APPROVED' ? 'Aprobado' : 
                                        vacationToDelete.status === 'PENDING' ? 'Pendiente' : 
                                        'Rechazado'}
              </Typography>
              
              <Typography variant="body1" sx={{ mt: 3, color: '#d32f2f' }}>
                ¿Estás seguro de que deseas borrar esta vacación?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancelar</Button>
          <Button 
            onClick={handleDeleteVacation} 
            color="error" 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Borrando...' : 'Borrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VacationCalendar;